import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { RouteProvider } from '../../providers/route/route';
import { OrderProvider } from '../../providers/order/order';
import { SendFlowProvider } from '../../providers/send-flow/send-flow';
import { RecipientProvider } from '../recipient/recipient';
import { IOrder } from '../../model/models/order/order-interface';
import { AgentProvider } from '../agent/agent';
import { SegmentProvider } from '../analytics/segment';
import { AnalyticsEventType, SegmentLocationNames } from '../../model/types/analytics.const';
import { LoggerProvider, LoggerEvent } from '../logger/logger';
import { DeliveryMethodType } from '../../model/types/delivery-method-type';
import { MobileWalletProvider } from '../mobile-wallet/mobile-wallet-provider';
import { TranslateService } from '@ngx-translate/core';
import { RmtPopupComponent } from '../../components/rmt-popup/rmt-popup';
import { ResourceProvider } from '../resource/resource';
import { PaymentProvider } from '../payment/payment';
import { HttpProvider } from '../../providers/http/http';
import { APP_CONFIG } from '../../environment';
import { FeatureFlagTypes, UtilsProvider } from '../myria/utils-provider';
import { VirtualPaymentType } from '../../model/types/vpa-type';
import { LoadingProvider } from '../../providers/loading/loading';
import { MOBILE_WALLET_VALIDATION } from '../../providers/myria/app-variables';
import { BankDepositSmartRoutingProvider } from '../bank-deposit-smart-routing/bank-deposit-smart-routing';

/** Whether the bank deposit branch routed the order, handed the customer a screen, or failed. */
type BankDepositSendAgainOutcome = 'routed' | 'reselecting' | 'failed';

/** The account details a repeated bank deposit is routed against, in the order status response's names. */
const BANK_ACCOUNT_DETAIL_FIELDS = ['bankName', 'bankAccountNumber', 'bankAccountNumberUnitary', 'bankAccountFormatId', 'bankAccountTypeId'];

/*
  Generated class for the SendAgainProvider provider.

  See https://angular.io/guide/dependency-injection for more info on providers
  and Angular DI.
*/
@Injectable()
export class SendAgainProvider {
  countryFrom: string;

  order: IOrder

  constructor(public http: HttpClient,
    private orderProvider: OrderProvider,
    private sendFlowProvider: SendFlowProvider,
    private agentProvider: AgentProvider,
    private analytics: SegmentProvider,
    private mobileWalletProvider: MobileWalletProvider,
    private paymentProvider: PaymentProvider,
    private resourceProvider: ResourceProvider,
    private routeProvider: RouteProvider,
    private recipientProvider: RecipientProvider,
    private translate: TranslateService,
    private httpProvider: HttpProvider,
    private utilsProvider: UtilsProvider,
    private bankDepositSmartRoutingProvider: BankDepositSmartRoutingProvider,
    private loading: LoadingProvider) {
    this.countryFrom = APP_CONFIG.region.defaultCountryFrom;
  }

  async processSendAgain(order, header, location = 'activity', fromER: boolean = false) {
    try {
      this.countryFrom = APP_CONFIG.region.defaultCountryFrom;
      this.order = JSON.parse(JSON.stringify(order)); // deep copy

      this.order.lastExchangeRate = order.exchangeRate;

      // Backward compat: reroute legacy PagoMovil bank deposit orders to VPA delivery method
      if (this.order.isPagoMovil && this.order.deliveryMethodInternal === DeliveryMethodType.BankDeposit) {
        this.order.deliveryMethodInternal = DeliveryMethodType.PagoMovil;
        this.order.virtualPaymentType = VirtualPaymentType.PagoMovil;
      }

      if (!this.order.filledAdditionalFields)
        this.order.filledAdditionalFields = {};

      // it seems there is a backend bug where the mobile wallet account number on the recipient can go missing - if we still have it in the order let's prefill it already
      if (this.order.recipient && this.order.recipient.mobileWalletAccountNo)
        this.order.filledAdditionalFields['mobileWalletAccountNo'] = this.order.recipient.mobileWalletAccountNo;

      const recipient = await this.orderProvider.checkRecipient(true, this.order);
      if (!recipient) {
        this.recipientUnavailableModal(this.order);
        return;
      }
      this.order.selectedDelivery = true;

      // TODO SKIP FOR NOW !!!!!!!!!!!!!!!!!!!!!!!!
      if (order.agentToIdSKIP) {
        let agentFound = false;

        const agents = await this.agentProvider.getAgents({
          countryTo: this.order.countryTo,
          deliveryMethod: this.order.deliveryMethod,
          agentToId: this.order.agentToId,
          state: recipient.state
        });
        if (agents) {
          for (let i = 0; i < agents.length; i++) {
            if (agents[0].id === this.order.agentToId) {
              agentFound = true;
              break;
            }
          }
        }
        if (!agentFound) {
          LoggerProvider.error(this, LoggerEvent.Popup, 'Agent location of transfer no longer available');
          this.orderProvider.showAgentNotFoundError();

          return;
        }
      }

      let valid = true;

      const pspPaymentMethod = this.resourceProvider.getPspPaymentMethod(this.order.paymentMethod);
      if (pspPaymentMethod) {
        const paymentMethods = await this.resourceProvider.getPaymentMethods();
        if(this.utilsProvider.isAppleOrGooglePaymentCreditAllowed() && this.paymentProvider.checkIfAppleOrGoogleCreditEnabled(paymentMethods)) {
          this.order.paymentMethod = this.paymentProvider.getAppleOrGoogleCreditValue(this.order.paymentMethod);
        }

        const validPayment = await this.orderProvider.checkPaymentMethod(null, false, header);
        if (!validPayment) {
          // when payment method is a PSP payment method it's not bound to the account hence ignore the check
          LoggerProvider.info(this, 'Payment method not available anymore - hence deselect it and continue');        
          this.order.cardAccount = null;
          this.order.bankAccount = null;
        }
      } else {
        valid = await this.orderProvider.checkPaymentMethod(null, true, header);
        if (!valid) {
          return;
        }  
      }

      if (this.order.deliveryMethodInternal === DeliveryMethodType.MobilePayment) {
        let wallet = null;
        try {
          var wallets;
          const isSmartRoutingEnabled = await this.mobileWalletProvider.isSmartRoutingEnable({
            CountryTo: this.order.countryTo,
            CurrencyTo: this.order.currencyTo,
            CurrencyFrom: this.order.currencyFrom,
            AmountFrom: this.order.amountFrom,
            AmountTo: this.order.amountTo
          });
          if (this.order.recipient.bankId && this.order.isSmartRouting && isSmartRoutingEnabled) {
            this.loading.show();
            const validations = await this.mobileWalletProvider.getWalletAccountSmartRoutingValidations(order, this.order.recipient.bankId);
            const res = await this.mobileWalletProvider.validateMobileWalletv2(Number(this.order.amountTo), this.order.recipient.mobileWalletAccountNo, validations.providerId, validations.entityId, this.order.currencyTo, validations.entityId, null, false, this.order.recipient.id, validations.correspondentId, validations.correspondentLocId);
            this.loading.hide();
            if (res.RetVal == MOBILE_WALLET_VALIDATION.SUCCESS) {
              wallet = {}
              wallet.EntityID = validations.entityId;
              wallet.ProviderId = validations.providerId;
              wallet.AccountFormatId = validations.accountFormatId;
            }
          }
          else if (isSmartRoutingEnabled) { // force legacy order to go through smart routing
            await this.sendFlowProvider.handleMobileWalletAndRiaWallet(order, false, false, true) // when resending legacy order and smart routing is enabled, we need to reselect the mobile wallet
            return;
          }
          else {
            wallets = await this.mobileWalletProvider.getMobileWallets(this.order, true); // true to ensure smart routing is not used even if flag is on
            if (wallets)
              wallet = wallets.find((wallet) =>
                (wallet.AggregatorId === this.order.agentToLocation.agentId && wallet.WalletId === this.order.agentToLocation.locationId));
          }
          if (wallet) {
            this.order.wallet = { ...wallet, accountNo: recipient.mobileWalletAccountNo };
            if (this.order.wallet.WalletId && this.order.wallet.AggregatorId)
              this.order.isSmartRouting = false;
            else if (this.order.wallet.EntityID)
              this.order.isSmartRouting = true;
          }
          else {
            this.orderProvider.showDeliveryMethodError(this.order)
            // RmtPopupComponent.showErrorMessageWithLogTranslation(this.translate.instant('page.errorMessage.ria_errormessage_repeattransfer_walletprovider'), ' ');
            // this.routeProvider.openModal(this.routeProvider.pages.CheckPricesPage);
            return;

          }
        } catch (ex) {
          this.loading.hide();
          LoggerProvider.exception(this, ex, LoggerEvent.GeneralException, "Exception while processing send again");
          this.showFallbackErrorModal();
          valid = false;
        }
      }

      if (!this.paymentProvider.isPaymentMethodAllowed(this.order.paymentMethod, this.order)) {
        LoggerProvider.info(this, 'Payment method not available anymore - hence not valid for send again');
        this.showFallbackErrorModal();
        valid = false;
      }

      if (this.order.deliveryMethodInternal === DeliveryMethodType.OfficePickup || this.order.deliveryMethodInternal === DeliveryMethodType.CashPickup) {
        try {
          // Remove agent location if agent or country is an open payment network
          if (this.order.agentTo && this.order.agentTo.isOpenPayment) {
            const skipRemoveAgentToLocation = await this.utilsProvider.hasFeatureFlag(FeatureFlagTypes.SkipRemoveAgentToLocation);
            if (skipRemoveAgentToLocation)
              LoggerProvider.info(this, `Skipped to remove agent to location for order ${order.orderNumber}`);
            else
              this.orderProvider.removeAgentToLocation(this.order);
          } else {
            const country = await this.resourceProvider.getCountryByValue(this.order.countryTo);
            if (country.isOpenPaymentCountry) {
              // Open payment country: without a specific location the order falls back to the country default.
              if (this.order.agentTo && this.order.agentToLocation) {
                if (!await this.validateAgentToLocation(order.orderNumber, false))
                  valid = false;
              } else {
                this.orderProvider.removeAgentToLocation(this.order);
              }
            } else if ((this.order.agentTo || this.order.agentToId) && !await this.validateAgentToLocation(order.orderNumber, true)) {
              // Location-specific payout: the backend rejects a repeated order without an active payout location.
              valid = false;
            }
          }
        } catch (ex) {
          LoggerProvider.exception(this, ex, LoggerEvent.GeneralException, "Exception while checking if destination is an open payment network");
          this.showFallbackErrorModal();
          valid = false;
        }
      }

      // Gated on valid: the bank deposit branch can hand the customer a screen that continues the flow on
      // its own, so a send again already failed above must not be offered one.
      if (valid && this.order.deliveryMethodInternal === DeliveryMethodType.BankDeposit) {
        const outcome = await this.resolveBankDepositRouting(recipient, header, location);
        // The customer is on the bank details screen; that screen continues the flow, not this one.
        if (outcome === 'reselecting')
          return;
        if (outcome === 'failed')
          valid = false;
      }
      if (valid) {
        this.analytics.trackEvent({
          eventName: AnalyticsEventType.TransferResendValidated, analyticsData: {
            location: location
          }
        });
        if (fromER) {
          this.routeProvider.openModal(this.routeProvider.pages.SendAgainPage, { 
            orderPromise: null,
            order: this.order,
            header: header,
            onBackCallback: async () => {
              await this.routeProvider.pop();
            }
          });
        } else {
          let newFlow = false;
          let countries = await this.utilsProvider.getFeatureFlagData(FeatureFlagTypes.SendAgainWithCalculatorCountries);
          if (countries && this.countryFrom && this.order.countryTo) {
            if(countries.indexOf(this.countryFrom + '_' + this.order.countryTo) >= 0) {
              LoggerProvider.info(this, `Send again with calculator for ${this.countryFrom} to ${this.order.countryTo} (${countries})`);
              newFlow = true;
            } else if(countries.indexOf(this.countryFrom + '_*') >= 0) {
              LoggerProvider.info(this, `Send again with calculator for ${this.countryFrom} (${countries})`);
              newFlow = true;
            } else if(countries.indexOf('*_' + this.order.countryTo) >= 0) {
              LoggerProvider.info(this, `Send again with calculator to ${this.order.countryTo} (${countries})`);
              newFlow = true;
            }
          }

          // POC SENDAGAIN WITH CALCULATOR
          // TODO Remove GT country logic when removing the POC
          if (newFlow) {
            this.routeProvider.openModal(this.routeProvider.pages.CheckPricesPage, { 
              orderPromise: null,
              order: this.order,
              header: header,
              sendAgain: true
            });
          } else {
            LoggerProvider.info(this, `Send again with old flow for ${this.countryFrom} to ${this.order.countryTo} (${countries})`);
            this.routeProvider.openModal(this.routeProvider.pages.SendAgainPage, {
              orderPromise: null,
              order: this.order,
              header: header
            });
          }
        }
      }
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.GeneralException, "Exception while processing send again");
      this.showFallbackErrorModal();
    }
  }

  /**
   * Checks the repeated order's payout location is still listed. Returns false when the customer was sent to
   * pick another one. With `required` a missing location blocks the send again instead of being dropped.
   */
  private async validateAgentToLocation(orderNumber: string, required: boolean): Promise<boolean> {
    const agentId = (this.order.agentToLocation && this.order.agentToLocation.agentId) || this.order.agentToId;
    const locationId = this.order.agentToLocation ? this.order.agentToLocation.locationId : null;
    if (!agentId || !locationId) {
      LoggerProvider.info(this, `Cannot validate agent location (agentId=${agentId}, locationId=${locationId}) for order ${orderNumber}`);
      this.orderProvider.removeAgentToLocation(this.order);
      if (!required)
        return true;
      this.orderProvider.showAgentNotFoundError();
      return false;
    }

    LoggerProvider.info(this, `Validating specific agent location ${locationId} for agent ${agentId} on order ${orderNumber}`);
    const locationDetails = await this.agentProvider.getAgentLocation(agentId, locationId);
    if (!locationDetails || locationDetails.length === 0) {
      LoggerProvider.error(this, LoggerEvent.Popup, `Agent location ${locationId} no longer available for order ${orderNumber}`);
      this.orderProvider.removeAgentToLocation(this.order);
      this.orderProvider.showAgentNotFoundError();
      return false;
    }
    return true;
  }

  /**
   * Routing for a repeated bank deposit. The routing decision belongs to the new transaction, so the
   * original order's correspondent is never replayed, and a smart-routing failure fails the send again
   * instead of quietly reverting to the legacy engine and mis-routing the payout.
   */
  private async resolveBankDepositRouting(liveRecipient, header, location): Promise<BankDepositSendAgainOutcome> {
    let smartRoutingEnabled: boolean;
    try {
      smartRoutingEnabled = await this.bankDepositSmartRoutingProvider.ensureSmartRoutingEnabledForOrder(this.order);
    } catch (ex) {
      // The gate is what says whether this corridor is smart routed at all, so an unresolved gate fails
      // the send again rather than falling through to the legacy engine and mis-routing the payout.
      LoggerProvider.exception(this, ex, LoggerEvent.GeneralException, 'Exception while resolving the smart routing corridor gate for send again - Bank Deposit');
      this.showFallbackErrorModal();
      return 'failed';
    }

    if (!smartRoutingEnabled)
      return this.resolveLegacyBankDepositAgent(liveRecipient, header, location);

    // The bank is the original order's, never the recipient's latest: the account number and format
    // being repeated belong to it, and it is the bank the transfer is created with.
    const bankId = this.order.recipient ? Number(this.order.recipient.bankId) : null;
    if (!bankId) {
      LoggerProvider.error(this, LoggerEvent.GeneralException, 'Cannot send again a bank deposit through smart routing without the original order\'s bank');
      this.showFallbackErrorModal();
      return 'failed';
    }

    // Smart routing qualifies a provider per bank and account format, so the account details have to be
    // in place before the provider is resolved.
    this.restoreOriginalBankAccountDetails(liveRecipient, bankId);
    if (!this.order.recipient.bankAccountFormatId)
      LoggerProvider.warning(this, LoggerEvent.GeneralException, 'Send again of a bank deposit with no account format to route on, smart routing will qualify against the bank default');

    // A legacy-created order carries a format and field names the legacy engine chose, so the customer
    // confirms them on the smart-routing form before the transfer is rebuilt.
    if (!this.order.isSmartRouting) {
      LoggerProvider.info(this, 'Send again of a legacy bank deposit in a smart routing corridor, reselecting the bank details');
      this.sendFlowProvider.openBankDepositForReselection(this.order, this.unmaskedBankRecipient(this.order.recipient), () => this.continueToSendAgainSummary(header, location));
      return 'reselecting';
    }

    try {
      this.loading.show();
      const provider = await this.bankDepositSmartRoutingProvider.resolveProvider({
        countryTo: this.order.countryTo,
        currencyTo: this.order.currencyTo,
        currencyFrom: this.order.currencyFrom,
        bankId,
        deliveryMethodId: Number(DeliveryMethodType.BANK_DEPOSIT_NUM_CODE),
        amountFrom: this.order.amountFrom,
        amountTo: this.order.amountTo,
        bankAccountFormatId: this.order.recipient.bankAccountFormatId ? Number(this.order.recipient.bankAccountFormatId) : undefined,
        recipientId: this.order.recipient.id
      });
      this.loading.hide();

      if (!provider) {
        LoggerProvider.error(this, LoggerEvent.GeneralException, `Smart routing resolved no provider for bank ${bankId} while sending again, failing instead of falling back to legacy routing`);
        this.showFallbackErrorModal();
        return 'failed';
      }

      this.sendFlowProvider.applyBankDepositRouting(this.order, provider);
      return 'routed';
    } catch (ex) {
      this.loading.hide();
      LoggerProvider.exception(this, ex, LoggerEvent.GeneralException, 'Exception while resolving the smart routing provider for send again - Bank Deposit');
      this.showFallbackErrorModal();
      return 'failed';
    }
  }

  /** Today's routing: the legacy engine resolves the payout agent from the recipient's bank. */
  private async resolveLegacyBankDepositAgent(liveRecipient, header, location): Promise<BankDepositSendAgainOutcome> {
    // Smart-routed account details can miss fields the legacy engine requires, so re-collect them on the legacy form.
    if (this.order.isSmartRouting) {
      if (this.order.recipient && this.order.recipient.bankId)
        this.restoreOriginalBankAccountDetails(liveRecipient, Number(this.order.recipient.bankId));
      this.sendFlowProvider.applyBankDepositRouting(this.order, null);
      LoggerProvider.info(this, 'Send again of a smart routed bank deposit with smart routing off, reselecting the bank details');
      this.sendFlowProvider.openBankDepositForReselection(this.order, this.unmaskedBankRecipient(this.order.recipient), () => this.continueToSendAgainSummary(header, location));
      return 'reselecting';
    }

    try {
      // To avoid Incorrect PayAgent & PayAgentLoc selected by Resend option
      const getNewPayAgent = await this.httpProvider.post(`${APP_CONFIG.API_URL}api/Agents/PayAgentIdInfo`, {
        DeliveryId: DeliveryMethodType.BANK_DEPOSIT_NUM_CODE,
        BankId: this.order.recipient.bankId,
        CountryTo: this.order.countryTo,
        CurrencyTo: this.order.currencyTo
      })
      if (getNewPayAgent)
        this.orderProvider.changeAgentToId(this.order, getNewPayAgent); // change agent id
      return 'routed';
    } catch (ex) {
      LoggerProvider.exception(this, ex, LoggerEvent.GeneralException, "Exception while processing send again - Bank Deposit");
      this.showFallbackErrorModal();
      return 'failed';
    }
  }

  /**
   * The original order's bank account details decide which provider qualifies, so they win over the
   * recipient's current ones. checkRecipient deliberately leaves the order's own copy alone in send
   * again mode, so only the values the order status response omitted are filled in — and only from a
   * recipient still saved against the same bank, since the details of another bank would mis-route.
   */
  private restoreOriginalBankAccountDetails(liveRecipient, bankId: number): void {
    const original = this.order.recipient;
    if (!original || !liveRecipient || Number(liveRecipient.bankId) !== bankId)
      return;

    BANK_ACCOUNT_DETAIL_FIELDS.forEach((field) => {
      if ((original[field] === null || original[field] === undefined || original[field] === '') && liveRecipient[field] !== undefined) {
        original[field] = liveRecipient[field];
        LoggerProvider.info(this, `Order status response carried no ${field} for send again, taking the recipient's saved value`);
      }
    });
  }

  /**
   * `api/tracking/order/status` masks the account number — `bankAccountNumber` comes back as
   * `***********4123` while the real value sits in `bankAccountNumberFull`. The bank form prefills from
   * whichever recipient it is handed, and `toSaveRecipientBankDepositRequestEntity` only substitutes the
   * unitary number, which is null for a Mexican deposit — so the masked value would be saved verbatim.
   *
   * Only the screen's copy is unmasked. The order's own recipient keeps the masked values everything
   * else displays, and a response without the full value falls back to what it does carry.
   */
  private unmaskedBankRecipient(recipient) {
    if (!recipient)
      return recipient;
    return {
      ...recipient,
      bankAccountNumber: recipient.bankAccountNumberFull || recipient.bankAccountNumber,
      bankAccountNumberUnitary: recipient.bankAccountNumberUnitaryFull || recipient.bankAccountNumberUnitary,
      bankRoutingCode: recipient.bankRoutingCodeFull || recipient.bankRoutingCode
    };
  }

  /**
   * Continues to the summary from inside the bank details modal, where the flow is already a nav stack.
   * The calculator step is deliberately skipped: the customer just confirmed the bank details for the
   * amount they are repeating, so the send again goes straight to the summary even in a corridor
   * SendAgainWithCalculatorCountries would otherwise route through CheckPricesPage.
   */
  private continueToSendAgainSummary(header, location): void {
    this.analytics.trackEvent({
      eventName: AnalyticsEventType.TransferResendValidated, analyticsData: {
        location: location
      }
    });
    // The bank screen re-saved the recipient and replaced the current order, so read it back rather
    // than passing the copy this provider started from.
    this.routeProvider.push(this.routeProvider.pages.SendAgainPage, {
      orderPromise: null,
      order: this.orderProvider.getCurrentOrder(),
      header
    });
  }

  showFallbackErrorModal() {
    const title = this.translate.instant('page.errorMessage.ria_errormessage_couldNotProcessHeader');
    const subtitle = this.translate.instant('page.errorMessage.ria_errormessage_couldNotProcessBody');
    const imgPath = 'assets/icon/yellow-warning-icon.svg';
    const primaryButtonTitle = this.translate.instant('global.newTransferBtn');
    const primaryButtonAction = () => {
      this.routeProvider.openModal(this.routeProvider.pages.CheckPricesPage);
    };
    this.orderProvider.showGenericErrorModal(imgPath, title, subtitle, primaryButtonTitle, primaryButtonAction);
  }

  recipientUnavailableModal(order) {
    this.routeProvider.openActionSheetNew(
      this.routeProvider.pages.ModalAlertPage,
      {
        image: 'assets/icons/recipient_NA.svg',
        title: this.translate.instant('page.errorMessage.ria_errormessage_norecipientheader'),
        subtitle: this.translate.instant('page.errorMessage.ria_errormessage_norecipientbody'),
        primaryButtonText: this.translate.instant('page.errorMessage.ria_errormessage_updaterecipientbutton'),
        showPrimaryButton: true,
        primaryAction: () => { this.redirectToSendToRecipientsPage(order) },
      }, null
    );
  }

  async redirectToSendToRecipientsPage(order, onContinue = null) {
    const allRecipients = await this.recipientProvider.getRecipients();
    const recipients = this.sendFlowProvider.filterCountryWiseRecipients(allRecipients)

    if (recipients && recipients.length === 0)
      this.sendFlowProvider.addRecipient(false, onContinue, true);
    else if (recipients && recipients.length > 0) {
      let params = {
        country: order.countryTo,
        goToActivity: true,
        onSelect: (recipient) => {
          order.recipient = recipient;
          this.orderProvider.setCurrentOrder(order)
          this.sendFlowProvider.selectedRecipient(recipient, false, SegmentLocationNames.Activity, null, null, onContinue);
        },
        onAdd: () => { this.sendFlowProvider.addRecipient(false, onContinue, true) }
      }
      this.routeProvider.push(this.routeProvider.pages.SendToRecipientsPage, params)
    }
  }
}



// WEBPACK FOOTER //
// ./src/providers/send-again/send-again.ts